import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { 
    Image, Upload, Loader2, CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import { apiRequest, API_URL } from '../../lib/utils';
import { toast } from 'sonner';

export default function BulkImageUpload() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingId, setUploadingId] = useState(null);
    const fileInputRefs = useRef({});

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        setLoading(true);
        try {
            const data = await apiRequest('/admin/products');
            setProducts(data);
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (productId, file) => {
        if (!file) return;

        setUploadingId(productId);
        try {
            // Upload the image
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('auth_token');
            const uploadResponse = await fetch(`${API_URL}/upload/image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!uploadResponse.ok) {
                throw new Error('Erreur upload');
            }

            const uploadData = await uploadResponse.json();
            // Remove /api prefix from url since API_URL already includes it
            const imageUrl = `${API_URL}${uploadData.url.replace('/api/', '/')}`;

            // Update the product with the new image URL
            await apiRequest(`/admin/products/${productId}`, {
                method: 'PUT',
                body: JSON.stringify({ image_url: imageUrl })
            });

            // Update local state
            setProducts(prev => prev.map(p => 
                p.product_id === productId 
                    ? { ...p, image_url: imageUrl }
                    : p
            ));

            toast.success('Image mise à jour !');
        } catch (error) {
            toast.error('Erreur: ' + error.message);
        } finally {
            setUploadingId(null);
        }
    };

    const triggerFileInput = (productId) => {
        if (fileInputRefs.current[productId]) {
            fileInputRefs.current[productId].click();
        }
    };

    const hasValidImage = (url) => {
        return url && !url.includes('undefined') && !url.includes('null');
    };

    const productsWithoutImages = products.filter(p => !hasValidImage(p.image_url));
    const productsWithImages = products.filter(p => hasValidImage(p.image_url));

    if (loading) {
        return (
            <Card className="glass rounded-2xl">
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                        <Image className="h-6 w-6 text-primary" />
                        Upload rapide des images
                    </h2>
                    <p className="text-muted-foreground">
                        Cliquez sur "Uploader" pour ajouter l'image de chaque produit
                    </p>
                </div>
                <Button onClick={loadProducts} variant="outline" className="rounded-full">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Actualiser
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
                <Card className="glass rounded-xl">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-red-500">{productsWithoutImages.length}</p>
                            <p className="text-sm text-muted-foreground">Sans image</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="glass rounded-xl">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-green-500">{productsWithImages.length}</p>
                            <p className="text-sm text-muted-foreground">Avec image</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Products without images first */}
            {productsWithoutImages.length > 0 && (
                <Card className="glass rounded-2xl border-red-500/30">
                    <CardHeader>
                        <CardTitle className="text-red-500 flex items-center gap-2">
                            <AlertCircle className="h-5 w-5" />
                            Produits sans image ({productsWithoutImages.length})
                        </CardTitle>
                        <CardDescription>
                            Ces produits n'ont pas d'image. Cliquez sur "Uploader" pour en ajouter une.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-3">
                            {productsWithoutImages.map(product => (
                                <div 
                                    key={product.product_id} 
                                    className="flex items-center gap-4 p-4 bg-red-500/5 rounded-xl border border-red-500/20"
                                >
                                    <div className="h-16 w-16 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                                        <Image className="h-8 w-8 text-muted-foreground/50" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold truncate">{product.name}</p>
                                        <p className="text-sm text-muted-foreground">{product.brand}</p>
                                    </div>
                                    <div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => fileInputRefs.current[product.product_id] = el}
                                            onChange={(e) => handleUpload(product.product_id, e.target.files?.[0])}
                                        />
                                        <Button
                                            onClick={() => triggerFileInput(product.product_id)}
                                            disabled={uploadingId === product.product_id}
                                            className="rounded-full"
                                        >
                                            {uploadingId === product.product_id ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Upload...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    Uploader
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Products with images */}
            {productsWithImages.length > 0 && (
                <Card className="glass rounded-2xl">
                    <CardHeader>
                        <CardTitle className="text-green-500 flex items-center gap-2">
                            <CheckCircle className="h-5 w-5" />
                            Produits avec image ({productsWithImages.length})
                        </CardTitle>
                        <CardDescription>
                            Ces produits ont déjà une image. Vous pouvez la remplacer si nécessaire.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-3">
                            {productsWithImages.map(product => (
                                <div 
                                    key={product.product_id} 
                                    className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl"
                                >
                                    <img 
                                        src={product.image_url} 
                                        alt={product.name}
                                        className="h-16 w-16 rounded-xl object-cover flex-shrink-0"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div 
                                        className="h-16 w-16 rounded-xl bg-muted items-center justify-center flex-shrink-0 hidden"
                                    >
                                        <Image className="h-8 w-8 text-muted-foreground/50" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold truncate">{product.name}</p>
                                        <p className="text-sm text-muted-foreground">{product.brand}</p>
                                    </div>
                                    <div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => fileInputRefs.current[product.product_id] = el}
                                            onChange={(e) => handleUpload(product.product_id, e.target.files?.[0])}
                                        />
                                        <Button
                                            variant="outline"
                                            onClick={() => triggerFileInput(product.product_id)}
                                            disabled={uploadingId === product.product_id}
                                            className="rounded-full"
                                        >
                                            {uploadingId === product.product_id ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Upload...
                                                </>
                                            ) : (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2" />
                                                    Remplacer
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
